import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  documentUrl,
  insertAtCursor,
  isWord,
  loadOffice,
  openDialog,
  scanTags,
  setReveal,
} from "./officeBridge";
import { fetchContext, getTokens, isAuthError, publishDraft, setTokens } from "./wordAddinApi";
import {
  cleanFieldName,
  fieldTag,
  filenameFromUrl,
  filterFields,
  initialTab,
  signatureTag,
  summarizeTags,
} from "./wordAddinModel";
import BriefReviewTab from "./BriefReviewTab";
import {
  Brand,
  Button,
  Dot,
  Grid,
  Input,
  List,
  Notice,
  Pane,
  Primary,
  Row,
  RowText,
  Section,
  SectionTitle,
  Select,
  Small,
  Tab,
  Tabs,
  Toggle,
} from "./paneStyles";

/*
 * The WHG Docs panel inside Word. Four jobs:
 *   Review      — the AI VA Attorney reads the open brief for house style
 *                 (only for people it is switched on for)
 *   Fields      — drop a template field tag at the cursor (existing or new)
 *   Signatures  — drop a signature / date / initials / printed-name tag for a signer
 *   Reveal tags — colour every tag in the document so it can be seen; the
 *                 colours are stripped again when the template is published
 */

const SIGNATURE_TEXT_COLOR = "#FFFFFF";

const WordAddinPane = () => {
  const [office, setOffice] = useState(null); // { host, platform }
  const [officeError, setOfficeError] = useState("");
  const [connected, setConnected] = useState(Boolean(getTokens()));
  const [connecting, setConnecting] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [ctxError, setCtxError] = useState("");
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [pickedTemplate, setPickedTemplate] = useState("");
  const [tab, setTab] = useState("fields");
  const [tabChosen, setTabChosen] = useState(false);
  const [query, setQuery] = useState("");
  const [newField, setNewField] = useState("");
  const [signer, setSigner] = useState(1);
  const [revealed, setRevealed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  const filename = useMemo(() => (office ? filenameFromUrl(documentUrl()) : ""), [office]);

  useEffect(() => {
    loadOffice()
      .then(setOffice)
      .catch((err) => setOfficeError(err.message));
  }, []);

  const say = useCallback((text, tone = "ok") => {
    setFlash({ text, tone });
    window.clearTimeout(say.timer);
    say.timer = window.setTimeout(() => setFlash(null), 3500);
  }, []);

  const loadContext = useCallback(
    async (formstackId) => {
      setLoadingCtx(true);
      setCtxError("");
      try {
        const params = { filename };
        if (formstackId) params.formstack_id = formstackId;
        const res = await fetchContext(params);
        setCtx(res.data);
      } catch (err) {
        if (isAuthError(err)) {
          setTokens(null);
          setConnected(false);
        } else {
          setCtxError(err?.response?.data?.error || "WHG Docs did not answer.");
        }
      } finally {
        setLoadingCtx(false);
      }
    },
    [filename]
  );

  useEffect(() => {
    if (office && connected) loadContext();
  }, [office, connected, loadContext]);

  // Until the person picks a tab, the pane opens on the job that fits the
  // file: Review for a brief, Fields for a template.
  useEffect(() => {
    if (ctx && !tabChosen) setTab(initialTab(ctx));
  }, [ctx, tabChosen]);

  const chooseTab = useCallback((name) => {
    setTabChosen(true);
    setTab(name);
  }, []);

  const dropConnection = useCallback(() => {
    setTokens(null);
    setConnected(false);
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      const tags = await scanTags();
      setSummary(summarizeTags(tags));
    } catch (err) {
      // Scanning is a convenience; never block the pane on it.
    }
  }, []);

  useEffect(() => {
    if (office && isWord(office)) refreshSummary();
  }, [office, refreshSummary]);

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const raw = await openDialog(`${window.location.origin}/word-addin/connect`);
      const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (msg?.type === "tokens" && msg.access) {
        setTokens({ access: msg.access, refresh: msg.refresh, user: msg.user });
        setConnected(true);
        say(`Connected as ${msg.user?.name || "you"}.`);
      } else {
        say("Sign-in did not complete.", "warn");
      }
    } catch (err) {
      say(err.message || "Sign-in did not complete.", "warn");
    } finally {
      setConnecting(false);
    }
  }, [say]);

  const insertField = useCallback(
    async (name) => {
      const tag = fieldTag(name);
      setBusy(tag);
      try {
        await insertAtCursor(tag, {
          highlight: revealed ? ctx?.reveal_highlights?.fields : null,
        });
        say(`Inserted ${tag}`);
        refreshSummary();
      } catch (err) {
        say("Could not insert — click where the tag should go, then try again.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, refreshSummary, revealed, say]
  );

  const insertNewField = useCallback(async () => {
    const name = cleanFieldName(newField);
    if (!name) return;
    await insertField(name);
    setNewField("");
  }, [insertField, newField]);

  const insertSignature = useCallback(
    async (type) => {
      const tag = signatureTag(type, signer);
      setBusy(tag);
      try {
        await insertAtCursor(tag, {
          color: SIGNATURE_TEXT_COLOR,
          highlight: revealed ? ctx?.reveal_highlights?.signatures : null,
        });
        say(`Inserted ${tag} (white text — turn on Reveal tags to see it)`);
        refreshSummary();
      } catch (err) {
        say("Could not insert — click where the tag should go, then try again.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, refreshSummary, revealed, say, signer]
  );

  const toggleReveal = useCallback(
    async (on) => {
      setBusy("reveal");
      try {
        const n = await setReveal(on, ctx?.reveal_highlights || {});
        setRevealed(on);
        say(on ? `Revealed ${n} tag${n === 1 ? "" : "s"}.` : "Tags hidden again.");
      } catch (err) {
        say("Could not change the highlighting.", "warn");
      } finally {
        setBusy("");
      }
    },
    [ctx, say]
  );

  // Word on the web blocks the browser's own confirm()/alert() inside an
  // add-in pane (they return immediately, as if cancelled), so the
  // confirmation is drawn in the pane instead.
  const publish = useCallback(async () => {
    if (!ctx?.template) return;
    setConfirmingPublish(false);
    setBusy("publish");
    try {
      await publishDraft(ctx.template.formstack_id);
      say("Published. Formstack now has this version.");
    } catch (err) {
      if (isAuthError(err)) {
        setTokens(null);
        setConnected(false);
      } else {
        say(err?.response?.data?.error || "Publish failed — Formstack still has the previous version.", "warn");
      }
    } finally {
      setBusy("");
    }
  }, [ctx, say]);

  // ---- render ----

  if (officeError) {
    return (
      <Pane>
        <Brand>WHG Docs</Brand>
        <Notice $bg="#fef2f2" $fg="#991b1b">{officeError}</Notice>
      </Pane>
    );
  }
  if (!office) {
    return (
      <Pane>
        <Brand>WHG Docs</Brand>
        <Small>Starting…</Small>
      </Pane>
    );
  }
  if (!isWord(office)) {
    return (
      <Pane data-testid="addin-not-in-word">
        <Brand>WHG Docs</Brand>
        <Notice>
          This page is the WHG Docs panel for Microsoft Word. Open a Word template from WHG Docs and use the WHG Docs
          button on Word&apos;s Home ribbon.
        </Notice>
      </Pane>
    );
  }

  if (!connected) {
    return (
      <Pane data-testid="addin-connect">
        <Brand>WHG Docs</Brand>
        <Notice>Sign in once to use WHG Docs in this document.</Notice>
        <Primary type="button" onClick={connect} disabled={connecting} data-testid="addin-connect-button">
          {connecting ? "Waiting for sign-in…" : "Connect to WHG Docs"}
        </Primary>
        {flash && <Notice $bg={flash.tone === "warn" ? "#fffbeb" : "#ecfdf5"}>{flash.text}</Notice>}
      </Pane>
    );
  }

  const template = ctx?.template;
  const fields = filterFields(ctx?.fields, query);
  const reviewEnabled = Boolean(ctx?.brief_review?.enabled);
  const onReview = reviewEnabled && tab === "review";

  return (
    <Pane data-testid="addin-pane">
      <Brand>
        <span>WHG Docs</span>
        {template && (
          <a
            href={`${window.location.origin}${template.editor_path}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 600 }}
          >
            Open in WHG Docs ↗
          </a>
        )}
      </Brand>

      {loadingCtx && <Small>Loading…</Small>}
      {ctxError && <Notice $bg="#fef2f2" $fg="#991b1b">{ctxError}</Notice>}

      {reviewEnabled && (
        <Tabs>
          <Tab type="button" $active={tab === "review"} onClick={() => chooseTab("review")} data-testid="addin-tab-review">
            Review
          </Tab>
          <Tab type="button" $active={tab === "fields"} onClick={() => chooseTab("fields")} data-testid="addin-tab-fields">
            Fields
          </Tab>
          <Tab type="button" $active={tab === "signatures"} onClick={() => chooseTab("signatures")} data-testid="addin-tab-signatures">
            Signatures
          </Tab>
        </Tabs>
      )}

      {onReview && <BriefReviewTab filename={filename} say={say} onAuthError={dropConnection} />}

      {!onReview && ctx && !ctx.matched && (
        <Section data-testid="addin-picker">
          <SectionTitle>Which template is this?</SectionTitle>
          <Small>
            This file isn&apos;t one WHG Docs placed in SharePoint{filename ? ` (${filename})` : ""}. Pick the template it
            belongs to, or just use the signature tools below.
          </Small>
          <Select value={pickedTemplate} onChange={(e) => setPickedTemplate(e.target.value)} data-testid="addin-template-select">
            <option value="">Choose a Word template…</option>
            {(ctx.templates || []).map((t) => (
              <option key={t.formstack_id} value={t.formstack_id}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button type="button" disabled={!pickedTemplate} onClick={() => loadContext(pickedTemplate)}>
            Use this template
          </Button>
        </Section>
      )}

      {!onReview && template && (
        <Small data-testid="addin-template-name">
          <strong style={{ color: "#0f172a" }}>{template.name}</strong>
        </Small>
      )}

      {!onReview && (
        <Section>
          <Toggle>
            <input
              type="checkbox"
              checked={revealed}
              disabled={busy === "reveal"}
              onChange={(e) => toggleReveal(e.target.checked)}
              data-testid="addin-reveal"
            />
            Reveal tags
          </Toggle>
          <Small data-testid="addin-summary">
            {summary
              ? `In this document: ${summary.field} field tag${summary.field === 1 ? "" : "s"} · ${summary.signature} signature tag${
                  summary.signature === 1 ? "" : "s"
                }${summary.signers.length ? ` (signer ${summary.signers.join(", ")})` : ""} · ${summary.conditional} condition marker${
                  summary.conditional === 1 ? "" : "s"
                }`
              : "Scanning the document…"}
          </Small>
          <Small>Colours are a view aid only — WHG Docs removes them when you publish.</Small>
        </Section>
      )}

      {!reviewEnabled && (
        <Tabs>
          <Tab type="button" $active={tab === "fields"} onClick={() => chooseTab("fields")} data-testid="addin-tab-fields">
            Fields
          </Tab>
          <Tab type="button" $active={tab === "signatures"} onClick={() => chooseTab("signatures")} data-testid="addin-tab-signatures">
            Signatures
          </Tab>
        </Tabs>
      )}

      {tab === "fields" && (
        <Section>
          <Small>Click in the document where the value should appear, then click a field.</Small>
          <Input
            placeholder="Search fields…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="addin-field-search"
          />
          <List>
            {fields.map((f) => (
              <Row key={f.field_name} type="button" onClick={() => insertField(f.field_name)} disabled={Boolean(busy)} data-testid="addin-field-row">
                <Dot $mapped={f.mapped} />
                <RowText>
                  <strong>{f.field_label || f.field_name}</strong>
                  <span>{f.mapped ? `${f.source} → ${f.sf_field_name}` : "Not mapped yet — map it in WHG Docs after publishing"}</span>
                </RowText>
              </Row>
            ))}
            {ctx?.matched && fields.length === 0 && <Small>No fields match.</Small>}
          </List>
          <SectionTitle>New field</SectionTitle>
          <div style={{ display: "flex", gap: 6 }}>
            <Input
              placeholder="e.g. Spouse Full Name"
              value={newField}
              onChange={(e) => setNewField(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && insertNewField()}
              data-testid="addin-new-field"
            />
            <Button type="button" onClick={insertNewField} disabled={!cleanFieldName(newField) || Boolean(busy)} data-testid="addin-insert-new-field">
              Insert
            </Button>
          </div>
          {cleanFieldName(newField) && <Small>Will insert {fieldTag(cleanFieldName(newField))}</Small>}
        </Section>
      )}

      {tab === "signatures" && (
        <Section>
          <Small>Click where the signer should sign, pick who, then what to place. Tags are written in white so they never print.</Small>
          <Select value={signer} onChange={(e) => setSigner(Number(e.target.value))} data-testid="addin-signer-select">
            {(ctx?.signers || [{ signer: 1, label: "Client" }, { signer: 2, label: "Attorney" }]).map((s) => (
              <option key={s.signer} value={s.signer}>
                {s.label} (signer {s.signer})
              </option>
            ))}
          </Select>
          <Grid>
            {(ctx?.tag_types || [
              { type: "sig", label: "Signature" },
              { type: "date", label: "Date signed" },
              { type: "initial", label: "Initials" },
              { type: "fullname", label: "Printed name" },
            ]).map((t) => (
              <Button key={t.type} type="button" onClick={() => insertSignature(t.type)} disabled={Boolean(busy)} data-testid={`addin-sig-${t.type}`}>
                {t.label}
              </Button>
            ))}
          </Grid>
          <Small>Will insert {signatureTag("sig", signer)} and friends. Signer 1 is always the client.</Small>
        </Section>
      )}

      {!onReview && template && (
        <Section>
          <SectionTitle>When you&apos;re done</SectionTitle>
          <Small>Word saves as you go. Publishing hands the saved file to Formstack so new documents use it.</Small>
          {confirmingPublish ? (
            <Notice data-testid="addin-publish-confirm">
              <div style={{ marginBottom: 8 }}>
                Publish <strong>{template.name}</strong> to Formstack? Make sure Word shows &quot;Saved&quot; first. Every
                document generated from now on will use this version.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Primary type="button" onClick={publish} data-testid="addin-publish-yes">
                  Yes, publish
                </Primary>
                <Button type="button" onClick={() => setConfirmingPublish(false)} data-testid="addin-publish-no">
                  Cancel
                </Button>
              </div>
            </Notice>
          ) : (
            <Primary
              type="button"
              onClick={() => setConfirmingPublish(true)}
              disabled={busy === "publish"}
              data-testid="addin-publish"
            >
              {busy === "publish" ? "Publishing…" : "Publish to Formstack"}
            </Primary>
          )}
        </Section>
      )}

      {flash && (
        <Notice $bg={flash.tone === "warn" ? "#fffbeb" : "#ecfdf5"} $fg={flash.tone === "warn" ? "#92400e" : "#065f46"} data-testid="addin-flash">
          {flash.text}
        </Notice>
      )}
    </Pane>
  );
};

export default WordAddinPane;
